import { defineNuxtPlugin } from '#app';
import { ofetch, type FetchOptions, type FetchContext } from 'ofetch';

import LeadModule from '~/repository/api/modules/lead';
import StorageModule from '~/repository/api/modules/storage';
import AuthModule from '~/repository/api/modules/auth';
import FlpModule from '~/repository/api/modules/flp';
import ProposalModule from '~/repository/api/modules/proposal';
import StudyModule from '~/repository/api/modules/study';
import PrevidenciaModule from '~/repository/api/modules/previdencia';
import PortabilidadeModule from '~/repository/api/modules/portabilidade';
import UserModule from '~/repository/api/modules/user';
import EmailTemplateModule from '~/repository/api/modules/email-template';
import MeetingModule from '~/repository/api/modules/meeting';
import ScoreModule from '~/repository/api/modules/score';
import AquariosModule from '~/repository/api/modules/aquarios';
import { IcatuLogsModule } from '~/repository/api/modules/icatu-logs';
import AuditModule from '~/repository/api/modules/audit';
import IntegracoesModule from '~/repository/api/modules/integracoes';

interface IApiInstance {
  lead: LeadModule;
  storage: StorageModule;
  auth: AuthModule;
  flp: FlpModule;
  proposal: ProposalModule;
  study: StudyModule;
  previdencia: PrevidenciaModule;
  portabilidade: PortabilidadeModule;
  user: UserModule;
  emailTemplate: EmailTemplateModule;
  meeting: MeetingModule;
  score: ScoreModule;
  aquarios: AquariosModule;
  icatuLogs: IcatuLogsModule;
  audit: AuditModule;
  integracoes: IntegracoesModule;
}

export default defineNuxtPlugin(() => {
  const fetchOptions: FetchOptions = {
    baseURL: '/api',
    credentials: 'include',

    async onResponseError({ request, response }: FetchContext) {
      if (response?.status !== 401 || !import.meta.client) return;

      const url = typeof request === 'string' ? request : request?.toString() || '';
      if (url.includes('/auth/')) return;

      // Guests (unauthenticated) use their own JWT for meeting endpoints — a 401
      // just means the guest token expired; let the calling code handle it.
      const { isAuthenticated, logout } = useAuth();
      if (!isAuthenticated.value) return;

      await logout();
    },
  };

  const apiFetcher = ofetch.create(fetchOptions);

  const modules: IApiInstance = {
    lead: new LeadModule(apiFetcher),
    storage: new StorageModule(apiFetcher),
    auth: new AuthModule(apiFetcher),
    flp: new FlpModule(apiFetcher),
    proposal: new ProposalModule(apiFetcher),
    study: new StudyModule(apiFetcher),
    previdencia: new PrevidenciaModule(apiFetcher),
    portabilidade: new PortabilidadeModule(apiFetcher),
    user: new UserModule(apiFetcher),
    emailTemplate: new EmailTemplateModule(apiFetcher),
    meeting: new MeetingModule(apiFetcher),
    score: new ScoreModule(apiFetcher),
    aquarios: new AquariosModule(apiFetcher),
    icatuLogs: new IcatuLogsModule(apiFetcher),
    audit: new AuditModule(apiFetcher),
    integracoes: new IntegracoesModule(apiFetcher),
  };

  return {
    provide: {
      api: modules,
    },
  };
});

declare module '#app' {
  interface NuxtApp {
    $api: IApiInstance;
  }
}

declare module 'vue' {
  interface ComponentCustomProperties {
    $api: IApiInstance;
  }
}
